import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@shared/components/confirmDialog/ConfirmDialog';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import BlankCard from '@shared/components/shared/BlankCard';
import Spinner from '@shared/components/spinner/Spinner';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';
import {
  useCampaignStructuredOutputGenerateControllerGenerateMutation,
  useCampaignStructuredOutputAttachControllerAttachMutation,
  useCampaignStructuredOutputListControllerListQuery,
  useStructuredOutputCreateControllerCreateMutation,
  useStructuredOutputUpdateControllerUpdateMutation,
} from '@shared/services/apiService/apiService';
import {
  parseStructuredOutputSchema,
  StructuredOutputFormFields,
  serializeStructuredOutputSchema,
  useStructuredOutputSchemaDraft,
  validateStructuredOutputSchemaEditorValue,
} from '@shared/components/StructuredOutputEditor';
import { getPrimaryCampaignStructuredOutput } from '@features/Admin/Campaign/utils/call-analysis-structured-output';

type CallAnalysisStructuredOutputSectionProps = {
  campaignId: string;
  isDisabled: boolean;
  isSquadCampaign?: boolean;
};

type DraftMode = 'empty' | 'generated' | 'existing';

type StructuredOutputEditorSnapshot = {
  name: string;
  description: string;
  instructions: string | null;
  schema: string;
};

const normalizeTextValue = (value: string | null | undefined) => value?.trim() ?? '';

const normalizeInstructionsValue = (value: string | null | undefined) => {
  const normalizedValue = value?.trim() ?? '';

  return normalizedValue ? normalizedValue : null;
};

const normalizeSchemaString = (schema: string) => {
  const parsedSchema = parseStructuredOutputSchema(schema);

  if (!parsedSchema.ok) {
    return schema.trim();
  }

  return JSON.stringify(serializeStructuredOutputSchema(parsedSchema.value));
};

const buildSnapshotFromValues = ({
  name,
  description,
  instructions,
  schema,
}: {
  name: string;
  description: string;
  instructions: string | null | undefined;
  schema: string;
}): StructuredOutputEditorSnapshot => ({
  name: normalizeTextValue(name),
  description: normalizeTextValue(description),
  instructions: normalizeInstructionsValue(instructions),
  schema: normalizeSchemaString(schema),
});

const CallAnalysisStructuredOutputSection: React.FC<CallAnalysisStructuredOutputSectionProps> = ({
  campaignId,
  isDisabled,
  isSquadCampaign = false,
}) => {
  const { t } = useTranslation();
  const { showError } = useErrorHandler();
  const [createStructuredOutput] = useStructuredOutputCreateControllerCreateMutation();
  const [updateStructuredOutput] = useStructuredOutputUpdateControllerUpdateMutation();
  const [attachStructuredOutput] = useCampaignStructuredOutputAttachControllerAttachMutation();
  const [generateStructuredOutput] =
    useCampaignStructuredOutputGenerateControllerGenerateMutation();
  const {
    data: structuredOutputs = [],
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useCampaignStructuredOutputListControllerListQuery(
    { id: campaignId },
    {
      skip: !campaignId,
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    schemaNode,
    rawJson,
    schemaError,
    mode,
    isJsonLocked,
    canRecoverJson,
    canSwitchToVisual,
    reset,
    initializeFromSchemaString,
    handleVisualChange,
    handleJsonChange,
    openJsonMode,
    openVisualMode,
    revertToLastValidJson,
    formatJson,
  } = useStructuredOutputSchemaDraft();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [draftMode, setDraftMode] = useState<DraftMode>('empty');
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isNameTouched, setIsNameTouched] = useState(false);
  const [isDescriptionTouched, setIsDescriptionTouched] = useState(false);
  const [isSchemaTouched, setIsSchemaTouched] = useState(false);
  const [showRegenerateConfirmDialog, setShowRegenerateConfirmDialog] = useState(false);
  const [baselineSnapshot, setBaselineSnapshot] = useState<StructuredOutputEditorSnapshot | null>(
    null,
  );

  const { primaryStructuredOutput, hasMultipleStructuredOutputs } = useMemo(
    () => getPrimaryCampaignStructuredOutput(structuredOutputs),
    [structuredOutputs],
  );

  const resetValidationState = useCallback(() => {
    setHasSubmitted(false);
    setIsNameTouched(false);
    setIsDescriptionTouched(false);
    setIsSchemaTouched(false);
  }, []);

  const clearDraft = useCallback(() => {
    setName('');
    setDescription('');
    setInstructions('');
    setDraftMode('empty');
    setBaselineSnapshot(null);
    reset();
    resetValidationState();
  }, [reset, resetValidationState]);

  const hydrateFromStructuredOutput = useCallback(
    (nextStructuredOutput: NonNullable<typeof primaryStructuredOutput>) => {
      setName(nextStructuredOutput.name ?? '');
      setDescription(nextStructuredOutput.description ?? '');
      setInstructions(nextStructuredOutput.instructions ?? '');
      setDraftMode('existing');
      setBaselineSnapshot(
        buildSnapshotFromValues({
          name: nextStructuredOutput.name ?? '',
          description: nextStructuredOutput.description ?? '',
          instructions: nextStructuredOutput.instructions ?? null,
          schema: nextStructuredOutput.schema,
        }),
      );
      initializeFromSchemaString(nextStructuredOutput.schema);
      resetValidationState();
    },
    [initializeFromSchemaString, resetValidationState],
  );

  const hasEditorDraft = draftMode !== 'empty' || Boolean(rawJson) || Boolean(schemaNode);

  const currentSnapshot = useMemo(() => {
    if (!hasEditorDraft) {
      return null;
    }

    const normalizedSchema =
      schemaNode !== null
        ? JSON.stringify(serializeStructuredOutputSchema(schemaNode))
        : normalizeSchemaString(rawJson);

    return {
      name: normalizeTextValue(name),
      description: normalizeTextValue(description),
      instructions: normalizeInstructionsValue(instructions),
      schema: normalizedSchema,
    } satisfies StructuredOutputEditorSnapshot;
  }, [description, hasEditorDraft, instructions, name, rawJson, schemaNode]);

  const isDirty = useMemo(() => {
    if (!currentSnapshot) {
      return false;
    }

    if (!baselineSnapshot) {
      return true;
    }

    return JSON.stringify(currentSnapshot) !== JSON.stringify(baselineSnapshot);
  }, [baselineSnapshot, currentSnapshot]);

  useEffect(() => {
    if (isDirty) {
      return;
    }

    if (primaryStructuredOutput) {
      hydrateFromStructuredOutput(primaryStructuredOutput);
      return;
    }

    if (draftMode !== 'generated') {
      clearDraft();
    }
  }, [clearDraft, draftMode, hydrateFromStructuredOutput, isDirty, primaryStructuredOutput]);

  const validationError = useMemo(() => {
    if (!hasEditorDraft) {
      return null;
    }

    if (!schemaNode) {
      return t('campaignPage.callAnalysis.editor.schemaJsonInvalid');
    }

    if (!name.trim()) {
      return t('campaignPage.callAnalysis.validation.nameRequired');
    }

    if (!description.trim()) {
      return t('campaignPage.callAnalysis.validation.descriptionRequired');
    }

    if (schemaError) {
      return t('campaignPage.callAnalysis.editor.schemaJsonInvalid');
    }

    return validateStructuredOutputSchemaEditorValue(schemaNode);
  }, [description, hasEditorDraft, name, schemaError, schemaNode, t]);

  const handleGenerate = useCallback(async () => {
    try {
      setIsGenerating(true);
      const generatedConfig = await generateStructuredOutput({
        id: campaignId,
      }).unwrap();

      setName(generatedConfig.name);
      setDescription(generatedConfig.description);
      setInstructions(generatedConfig.instructions);
      initializeFromSchemaString(generatedConfig.schema);
      setDraftMode('generated');
      setBaselineSnapshot(null);
      resetValidationState();
    } catch (error) {
      showError({
        title: t('campaignPage.callAnalysis.messages.generateErrorTitle'),
        error,
        suggestion: t('campaignPage.callAnalysis.messages.generateErrorSuggestion'),
      });
    } finally {
      setIsGenerating(false);
    }
  }, [
    campaignId,
    generateStructuredOutput,
    initializeFromSchemaString,
    resetValidationState,
    showError,
    t,
  ]);

  const handleRegenerate = () => {
    if (isDisabled || isBusy) {
      return;
    }

    if (isDirty) {
      setShowRegenerateConfirmDialog(true);
      return;
    }

    void handleGenerate();
  };

  const handleRegenerateConfirm = async () => {
    setShowRegenerateConfirmDialog(false);
    await handleGenerate();
  };

  const handleCancel = () => {
    if (isSaving || isGenerating) {
      return;
    }

    if (primaryStructuredOutput) {
      hydrateFromStructuredOutput(primaryStructuredOutput);
      return;
    }

    clearDraft();
  };

  const handleSave = async () => {
    setHasSubmitted(true);

    if (!schemaNode || validationError) {
      return;
    }

    try {
      setIsSaving(true);

      const payload = {
        name: name.trim(),
        description: description.trim(),
        instructions: instructions.trim() ? instructions.trim() : null,
        schema: serializeStructuredOutputSchema(schemaNode),
      };

      if (primaryStructuredOutput?.id) {
        await updateStructuredOutput({
          id: primaryStructuredOutput.id,
          structuredOutputUpdateRequestDto: payload,
        }).unwrap();
      } else {
        const createdStructuredOutput = await createStructuredOutput({
          structuredOutputCreateRequestDto: payload,
        }).unwrap();

        await attachStructuredOutput({
          id: campaignId,
          campaignStructuredOutputAttachRequestDto: {
            structuredOutputId: createdStructuredOutput.id,
          },
        }).unwrap();
      }

      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('common.saved'),
          description: t('campaignPage.callAnalysis.messages.saved'),
        },
      });

      setBaselineSnapshot(
        buildSnapshotFromValues({
          name: payload.name,
          description: payload.description,
          instructions: payload.instructions,
          schema: JSON.stringify(payload.schema),
        }),
      );
      setDraftMode('existing');
      await refetch();
    } catch (error) {
      showError({
        title: t('campaignPage.callAnalysis.messages.saveErrorTitle'),
        error,
        suggestion: t('campaignPage.callAnalysis.messages.saveErrorSuggestion'),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const isBusy = isSaving || isGenerating;
  const callAnalysisHelperText = t(
    isSquadCampaign
      ? 'campaignPage.callAnalysis.squadEditorHelperText'
      : 'campaignPage.callAnalysis.editorHelper',
  );
  const callAnalysisEmptyDescription = t(
    isSquadCampaign
      ? 'campaignPage.callAnalysis.squadHelperText'
      : 'campaignPage.callAnalysis.emptyDescription',
  );

  if (isLoading) {
    return <Spinner />;
  }

  if (isError) {
    return (
      <BlankCard>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h6">{t('campaignPage.callAnalysis.errorTitle')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('common.errorMessage')}
          </Typography>
          <Box>
            <Button variant="contained" onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          </Box>
        </Box>
      </BlankCard>
    );
  }

  if (!hasEditorDraft) {
    return (
      <BlankCard>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h6">{t('campaignPage.callAnalysis.emptyTitle')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {callAnalysisEmptyDescription}
          </Typography>
          <Box>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={isDisabled || isGenerating || isFetching}>
              {isGenerating ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t('campaignPage.callAnalysis.generateButton')
              )}
            </Button>
          </Box>
        </Box>
      </BlankCard>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        gap={2}>
        <Box>
          {isFetching ? (
            <Typography variant="caption" color="text.secondary">
              {t('campaignPage.callAnalysis.syncing')}
            </Typography>
          ) : null}
        </Box>
        <Stack direction="row" spacing={1} flexShrink={0} justifyContent="flex-end">
          <Button variant="outlined" onClick={handleRegenerate} disabled={isDisabled || isBusy}>
            {isGenerating ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('campaignPage.callAnalysis.regenerateButton')
            )}
          </Button>
          <Button variant="outlined" onClick={handleCancel} disabled={!isDirty || isBusy}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isDisabled || isBusy || !isDirty || Boolean(validationError)}>
            {isSaving ? <CircularProgress size={20} color="inherit" /> : t('action.save')}
          </Button>
        </Stack>
      </Stack>

      {hasMultipleStructuredOutputs ? (
        <Alert severity="warning">{t('campaignPage.callAnalysis.multipleOutputsWarning')}</Alert>
      ) : null}

      {draftMode === 'generated' ? (
        <Alert severity="info">{t('campaignPage.callAnalysis.generatedNotice')}</Alert>
      ) : null}

      <StructuredOutputFormFields
        name={name}
        description={description}
        instructions={instructions}
        schema={schemaNode}
        schemaError={schemaError}
        schemaMode={mode}
        rawSchemaJson={rawJson}
        canRecoverSchemaJson={canRecoverJson}
        canSwitchToVisualSchema={canSwitchToVisual}
        isSchemaJsonLocked={isJsonLocked}
        isSubmitting={isBusy || isDisabled}
        topHelperText={callAnalysisHelperText}
        showNameValidation={isNameTouched || hasSubmitted}
        showDescriptionValidation={isDescriptionTouched || hasSubmitted}
        showSchemaValidation={isSchemaTouched || hasSubmitted}
        onNameChange={(value) => {
          setName(value);
          setIsNameTouched(true);
        }}
        onDescriptionChange={(value) => {
          setDescription(value);
          setIsDescriptionTouched(true);
        }}
        onInstructionsChange={setInstructions}
        onSchemaChange={(value) => {
          handleVisualChange(value);
          setIsSchemaTouched(true);
        }}
        onSchemaJsonChange={(value) => {
          handleJsonChange(value);
          setIsSchemaTouched(true);
        }}
        onOpenSchemaJson={openJsonMode}
        onBackToVisualSchema={openVisualMode}
        onFormatSchemaJson={formatJson}
        onRevertSchemaJson={revertToLastValidJson}
        onNameBlur={() => setIsNameTouched(true)}
        onDescriptionBlur={() => setIsDescriptionTouched(true)}
      />

      <ConfirmDialog
        showDialogFlag={showRegenerateConfirmDialog}
        handleClose={() => setShowRegenerateConfirmDialog(false)}
        dialogTitle="campaignPage.callAnalysis.regenerateConfirmTitle"
        dialogContentText={t('campaignPage.callAnalysis.regenerateConfirmDescription')}
        showDialog={setShowRegenerateConfirmDialog}
        confirmDialog={handleRegenerateConfirm}
      />
    </Stack>
  );
};

export default CallAnalysisStructuredOutputSection;
